import { TeamSettingsQuery } from '@prophecy/queries/pages/UserSettings/queries/team';
import { theme } from '@prophecy/theme';
import { Dialog, Spinner, Stack, Text } from '@prophecy/ui-v3';
import { useLoadableCallback } from '@prophecy/utils/react/hooks';
import { useMemo } from 'react';

import { useCreditsLabel } from '../context/credits';
import { useEditionConfig } from '../context/edition';
import { useUser } from '../context/user';
import { activateUser, completeLogoutAndReloadPage, getCreditUsage } from '../data/apis/api';
import { useRestQuery, useGraphQlQuery } from '../data/util';
import { Entity } from './types/Entity';
type CreditUsage = { tenantName: string; totalUsers: number; creditsCostPerAdditionalUser: number };

export const InactiveUserDialog = () => {
  const creditLabelWithOEM = useCreditsLabel();
  const { data, isLoading } = useRestQuery<CreditUsage>(['get-credit-usage'], () => getCreditUsage(), {
    retry: false,
    refetchOnWindowFocus: false
  });

  const [isLoggingOut, logout] = useLoadableCallback(completeLogoutAndReloadPage);
  const [isActivatingUser, activate] = useLoadableCallback(activateUser);
  if (isLoading || !data) return <Spinner spinning centered />;

  return (
    <Dialog
      open
      size='l'
      title='Add Another User'
      closable={false}
      onOk={() => activate()}
      closeButton={{ loading: isLoggingOut, onClick: () => logout(), 'aria-label': 'Close dialog and logout' }}
      okButton={{ loading: isActivatingUser, 'aria-label': 'Activate User' }}
      data-test-id='add-another-user-dialog'>
      <Stack padding={`${theme.spaces.x24} 0`}>
        <Text level='sm' tone={theme.textColor.tertiary}>
          {data.data.tenantName} is licensed for {data.data.totalUsers} users for Prophecy for SAP Sales Performance
          Management, all of which are currently in use. If you continue to log in, your license will be extended to{' '}
          {data.data.totalUsers + 1} users at the cost of {data.data.creditsCostPerAdditionalUser} {creditLabelWithOEM}
          s.
        </Text>
      </Stack>
    </Dialog>
  );
};

export const RejectedUserDialog = () => {
  const { data, isLoading } = useRestQuery<CreditUsage>(['get-credit-usage'], () => getCreditUsage(), {
    retry: false,
    refetchOnWindowFocus: false
  });
  const { globalTeamName } = useEditionConfig();
  const user = useUser()?.user;
  const globalTeamId = user?.teams.find((team) => team.name === globalTeamName)?._id;

  const { data: teamData } = useGraphQlQuery(
    [Entity.Team, Entity.Team + 'Detail', { uid: globalTeamId }],
    TeamSettingsQuery,
    { enabled: Boolean(globalTeamId) },
    { uid: globalTeamId as string }
  );

  const adminEmails = useMemo(() => {
    const teams = teamData?.User?.teams;
    if (!teams || teams.length === 0) return [];
    const admins = teams[0]?.admins || [];
    return admins.map((admin) => admin.email).filter(Boolean);
  }, [teamData]);

  const mailtoLink = useMemo(() => {
    if (adminEmails.length === 0) return '';
    return `mailto:${adminEmails.join(',')}?subject=${encodeURIComponent('Account Access Request')}&body=${encodeURIComponent('Hi,\n\nI am requesting access to the Prophecy platform. My account was rejected and I would like to discuss reactivation.\n\nThank you.')}`;
  }, [adminEmails]);

  const [isLoggingOut, logout] = useLoadableCallback(completeLogoutAndReloadPage);
  if (isLoading || !data) return <Spinner spinning centered />;

  return (
    <Dialog
      open
      size='l'
      title='Account access rejected'
      closable={false}
      onOk={() => logout()}
      okButton={{ loading: isLoggingOut, 'aria-label': 'Logout' }}
      closeButton={null}
      data-test-id='rejected-user-dialog'>
      <Stack padding={`${theme.spaces.x24} 0`} gap={theme.spaces.x12}>
        <Text level='sm' tone={theme.textColor.tertiary}>
          {data.data.tenantName} has rejected access to your account. Please contact your administrator.
        </Text>
        {mailtoLink && (
          <Text level='sm'>
            <a href={mailtoLink} data-test-id='rejected-user-contact-admin-link'>
              Send email to administrator{adminEmails.length > 1 ? 's' : ''}
            </a>
          </Text>
        )}
      </Stack>
    </Dialog>
  );
};
